// src/components/messaging/MessagingModal.tsx - Fixed version
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext.tsx';

interface Conversation {
  conversation_id: string;
  title: string;
  participants: Array<{
    id: string;
    username: string;
    first_name: string;
    last_name: string;
    profile_picture?: string; // ← ADD THIS LINE
  }>;
  last_message?: {
    content: string;
    sender: string;
    created_at: string;
    message_type: string;
  };
  unread_count: number;
  last_activity: string;
}

interface MessagingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConversationSelect: (conversationId: string) => void;
}

export const MessagingModal: React.FC<MessagingModalProps> = ({
  isOpen,
  onClose,
  onConversationSelect
}) => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchConversations();
    }
  }, [isOpen]);

  const fetchConversations = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch('https://aztrainersbackend.onrender.com/api/conversations/', {
        headers: {
          'Authorization': token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setConversations(data.conversations);
      }
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.abs(now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  const getOtherParticipant = (conv: Conversation) => {
    return conv.participants.find(p => p.id !== user?.id);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-800">Messages</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Conversations List */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9CAF88] mx-auto"></div>
              <p className="text-gray-500 mt-2">Loading conversations...</p>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-8 text-center">
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mt-2">No conversations yet</h3>
              <p className="text-gray-500">Start messaging with other users to see conversations here.</p>
            </div>
          ) : (
            conversations.map((conversation) => {
              const otherParticipant = getOtherParticipant(conversation);
              return (
                <div
                  key={conversation.conversation_id}
                  onClick={() => onConversationSelect(conversation.conversation_id)}
                  className="p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center space-x-3">
                    {/* Avatar - FIXED TO SHOW PROFILE PICTURES */}
                    <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                      {otherParticipant?.profile_picture ? (
                        <img
                          src={
                            otherParticipant.profile_picture.startsWith('http')
                            ? otherParticipant.profile_picture // Already a full URL
                            : `https://aztrainersbackend.onrender.com${otherParticipant.profile_picture}` // Relative path
                          }
                          alt=""
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // If image fails to load, hide it and show initials
                            const imgElement = e.target as HTMLImageElement;
                            const parent = imgElement.parentElement;
                            if (parent) {
                              imgElement.style.display = 'none';
                              parent.innerHTML = `
                                <div class="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold">
                                  ${otherParticipant?.first_name?.[0] || otherParticipant?.username[0] || ''}${otherParticipant?.last_name?.[0] || ''}
                                </div>
                              `;
                            }
                          }}
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#E6A756] to-[#D2824C] flex items-center justify-center text-white font-bold">
                          {otherParticipant?.first_name?.[0] || otherParticipant?.username[0]}
                          {otherParticipant?.last_name?.[0] || ''}
                        </div>
                      )}
                    </div>

                    {/* Conversation Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {otherParticipant ? 
                            `${otherParticipant.first_name} ${otherParticipant.last_name}`.trim() || otherParticipant.username
                            : conversation.title
                          }
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatTime(conversation.last_activity)}
                        </p>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500 truncate">
                          {conversation.last_message ? (
                            <>
                              {conversation.last_message.sender === user?.username ? 'You: ' : ''}
                              {conversation.last_message.message_type === 'image' ? '📷 Photo' :
                               conversation.last_message.message_type === 'file' ? '📎 File' :
                               conversation.last_message.content}
                            </>
                          ) : (
                            'No messages yet'
                          )}
                        </p>
                        
                        {conversation.unread_count > 0 && (
                          <span className="bg-[#9CAF88] text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium ml-2">
                            {conversation.unread_count > 99 ? '99+' : conversation.unread_count}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};